/* =============================================================================
   strutz-cart-enhancements-v3.js
   Rewritten to match the CURRENT cart-drawer Liquid + the 3 BXGY discounts.

   MODEL (NTZ pairs in cart -> rewards, each zeroed by an automatic BXGY discount):
     3 NTZ -> Mystery Pair        (handle: free-sticker-copy,      $29)
     4 NTZ -> + 2nd Mystery Pair  (handle: mystery-pair-2,         $29)
     5 NTZ -> + Hawaiian Shirt    (handle: mystery-hawaiian-shirt, $35)

   Reward flow = MANUAL ADD: customer picks a size in the rewards panel, JS adds
   that variant; the matching BXGY discount makes it $0.  JS AUTO-REMOVES a reward
   if NTZ qty drops below its tier (otherwise the discount stops applying and the
   customer would be charged for it).

   Free shipping = the existing $49 dollar-based DISPLAY bar only. It is NOT a
   qty milestone and NOT a discount. (See README notes in chat.)
   ========================================================================== */
(() => {
  'use strict';

  /* ----------------------------- CONFIG ----------------------------------- */
  const DEFAULTS = {
    freeShippingCents: 4900,            // $49 display-only motivator
    ntzHandle: 'ntz-collection',        // collection that counts toward tiers
    btzHandle: 'btz',
    allHandle: 'all',
    maxTier: 5,
    // Reward products. `tier` = NTZ qty required. `slot` = stable id for the line property.
    rewards: [
      { slot: 'pair1', tier: 3, handle: 'free-sticker-copy',      name: 'Free Mystery Pair',          priceCents: 2900 },
      { slot: 'pair2', tier: 4, handle: 'mystery-pair-2',         name: '2nd Free Mystery Pair',      priceCents: 2900 },
      { slot: 'shirt', tier: 5, handle: 'mystery-hawaiian-shirt', name: 'Free Mystery Hawaiian Shirt', priceCents: 3500 }
    ]
  };

  const selectors = {
    root: '[data-strutz-cart-enhancements]',
    lineItem: '[data-strutz-cart-item]',
    progress: '[data-strutz-progress]',
    progressFill: '[data-strutz-progress-fill]',
    status: '[data-strutz-progress-status]',
    milestone: '[data-strutz-milestone]',                 // tier-3 / tier-4 / tier-5
    bundleRewards: '[data-strutz-bundle-rewards]',
    tierRow: '[data-tier-min]',
    tierCheck: '[data-strutz-tier-check]',
    footerSavings: '[data-strutz-footer-savings]',
    footerTotal: '[data-strutz-footer-total]',
    checkoutTotal: '[data-strutz-checkout-total]',
    mysteryMessage: '[data-strutz-mystery-message]',
    mysterySelector: '[data-strutz-mystery-selector]',
    upsells: '[data-strutz-upsells]',
    checkout: '[data-strutz-checkout]'
  };

  const state = {
    cart: null,
    products: new Map(),   // handle -> product json
    syncing: false,        // guards auto-remove loop
    busy: false,           // guards add/remove button spam
    skipEnforce: false,    // temporarily skip enforcement after add/enforce
    upsellCache: new Map(),
    lastUpsellKey: ''
  };

  function getConfig() {
    const root = document.querySelector(selectors.root);
    const cfg = JSON.parse(JSON.stringify(DEFAULTS));
    if (!root) return cfg;
    cfg.freeShippingCents = intOr(root.dataset.freeShippingCents, DEFAULTS.freeShippingCents);
    cfg.ntzHandle = (root.dataset.ntzCollectionHandle || cfg.ntzHandle).trim() || cfg.ntzHandle;
    cfg.btzHandle = (root.dataset.btzCollectionHandle || cfg.btzHandle).trim() || cfg.btzHandle;
    cfg.allHandle = (root.dataset.allCollectionHandle || cfg.allHandle).trim() || cfg.allHandle;
    return cfg;
  }
  const rewardHandles = () => DEFAULTS.rewards.map((r) => r.handle);

  /* --------------------------- THEME PLUMBING ----------------------------- */
  /* (kept identical to the working v2 so theme integration is unchanged)     */
  function intOr(v, fallback) { const n = parseInt(v, 10); return Number.isFinite(n) ? n : fallback; }

  function formatMoney(cents) {
    const value = Math.max(0, Number(cents) || 0);
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      const fmt = window.theme?.settings?.moneyWithCurrencyFormat || window.theme?.settings?.moneyFormat;
      return window.Shopify.formatMoney(value, fmt);
    }
    const currency = state.cart?.currency || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US',
        { style: 'currency', currency, minimumFractionDigits: 2 }).format(value / 100);
    } catch (_e) { return `$${(value / 100).toFixed(2)}`; }
  }

  function escapeHtml(value) {
    return String(value ?? '').replace(/[&<>'"]/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#039;', '"': '&quot;' }[c]));
  }

  function getBundledSections() {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections',
      { bubbles: true, detail: { sections } }));
    return sections.filter(Boolean);
  }

  function fetchConfig(body) {
    if (window.theme?.utils?.fetchConfig) {
      return { ...window.theme.utils.fetchConfig(), body: JSON.stringify(body) };
    }
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    };
  }

  function publishCartUpdate(cart, source = 'strutz-cart') {
    state.cart = cart;
    // Prefer the theme's native cartUpdate event when section HTML is available;
    // cart.js swaps the drawer markup and then emits cart:updated for v3 reward/progress recalculation.
    if (window.theme?.pubsub?.publish && window.theme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate && cart.sections && typeof cart.sections === 'object' && !Array.isArray(cart.sections)) {
      window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source, cart });
    } else {
      // Fallback: dispatch cart:refresh which makes the theme re-fetch section HTML itself.
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, source } }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true, source } }));
    }
  }

  async function fetchFreshCartJson() {
    const cartRoute = window.theme?.routes?.cart_url || '/cart';
    const res = await fetch(`${cartRoute}.js`, {
      ...((window.theme?.utils?.fetchConfig && window.theme.utils.fetchConfig('json', 'GET')) || {}),
      credentials: 'same-origin'
    });
    if (!res.ok) throw new Error('Unable to fetch updated cart');
    return res.json();
  }

  async function addToCartAndPublish(payload, source = 'strutz-cart') {
    const route = window.theme?.routes?.cart_add_url || '/cart/add.js';
    const addPayload = { ...payload, sections: payload.sections || getBundledSections() };
    const response = await fetch(route, fetchConfig(addPayload));
    const parsedState = await response.json();

    if (!response.ok || parsedState.status) {
      throw new Error(parsedState.description || parsedState.message || 'Unable to add item to cart');
    }

    // Match the native ProductForm flow: wait for /cart/add.js, fetch a fresh cart JSON,
    // attach the section HTML produced by the add response, then publish cartUpdate.
    const cart = await fetchFreshCartJson();
    cart.sections = parsedState.sections || {};
    publishCartUpdate(cart, source);
    return cart;
  }

  async function getCart() {
    if (state.cart && Array.isArray(state.cart.items)) return state.cart;
    const route = window.theme?.routes?.cart_url || '/cart';
    const res = await fetch(`${route}.js`, { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Unable to fetch cart');
    state.cart = await res.json();
    return state.cart;
  }

  async function fetchCartWithSections(sections) {
    const cartRoute = window.theme?.routes?.cart_url || '/cart';
    // Fetch cart JSON and section HTML in parallel for proper theme re-render
    let url = `${cartRoute}.js`;
    if (sections && sections.length) {
      url += `?sections=${sections.join(',')}`;
    }
    const res = await fetch(url, { credentials: 'same-origin' });
    const cart = await res.json();
    // If sections weren't returned inline (older Shopify), fetch them separately
    if (sections && sections.length && !cart.sections) {
      try {
        const sectionRes = await fetch(`${cartRoute}?sections=${sections.join(',')}`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
        if (sectionRes.ok) {
          const sectionData = await sectionRes.json();
          cart.sections = sectionData;
        }
      } catch (e) { /* sections unavailable, theme will handle gracefully */ }
    }
    return cart;
  }

  async function changeLine(key, quantity, publish = true) {
    const route = window.theme?.routes?.cart_change_url || '/cart/change.js';
    const res = await fetch(route, fetchConfig({ id: key, quantity, sections: getBundledSections() }));
    const parsed = await res.json();
    if (publish) publishCartUpdate(parsed, 'strutz-reward');
    return parsed;
  }

  async function getProduct(handle) {
    if (state.products.has(handle)) return state.products.get(handle);
    const root = window.theme?.routes?.root_url || '/';
    const res = await fetch(`${root}products/${handle}.js`, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`Unable to load product: ${handle}`);
    const product = await res.json();
    state.products.set(handle, product);
    return product;
  }

  function getVariantLabel(product, variant) {
    const opts = [variant.option1, variant.option2, variant.option3].filter(Boolean);
    if (!opts.length || opts.every((o) => o === 'Default Title')) return 'One size';
    const sizeIdx = (product.options || []).findIndex((o) =>
      String(o.name || o).toLowerCase().includes('size'));
    return sizeIdx >= 0 && opts[sizeIdx] ? opts[sizeIdx] : opts.join(' / ');
  }

  /* ------------------------------- METRICS -------------------------------- */
  function domItems() {
    return Array.from(document.querySelectorAll(selectors.lineItem)).map((node) => ({
      key: node.dataset.key,
      handle: node.dataset.handle || '',
      variantId: Number(node.dataset.variantId || 0),
      quantity: Number(node.dataset.quantity || 0),
      price: Number(node.dataset.price || 0),
      subscription: node.dataset.subscription === 'true',
      ntz: node.dataset.ntzItem === 'true',
      rewardSlot: node.dataset.rewardSlot || ''
    }));
  }

  function itemsFromCart(cart) {
    const dom = domItems();
    if (dom.length) return dom;
    return (cart?.items || []).map((it) => ({
      key: it.key,
      handle: it.handle || '',
      variantId: it.variant_id,
      quantity: it.quantity,
      price: it.original_price || it.price || 0,
      subscription: Boolean(it.selling_plan_allocation),
      ntz: false, // can't know collections from cart.js; DOM path is the source of truth
      rewardSlot: it.properties?._strutz_reward_slot || ''
    }));
  }

  const isReward = (item) => rewardHandles().includes(item.handle) || Boolean(item.rewardSlot);

  function getMetrics(cart = state.cart) {
    const config = getConfig();
    const items = itemsFromCart(cart);

    // NTZ qty = NTZ collection items, excluding rewards and subscriptions
    const ntzQty = items.reduce((sum, it) =>
      sum + ((it.ntz && !isReward(it) && !it.subscription) ? it.quantity : 0), 0);

    // Which reward slots are currently in the cart, keyed by slot
    const rewardLines = {};
    items.forEach((it) => {
      if (!isReward(it)) return;
      const def = DEFAULTS.rewards.find((r) => r.handle === it.handle) ||
                  DEFAULTS.rewards.find((r) => r.slot === it.rewardSlot);
      if (def) rewardLines[def.slot] = it;
    });

    const subtotal = cart?.items_subtotal_price ?? cart?.total_price ?? 0;
    const total = cart?.total_price ?? subtotal;
    const savings = cart?.total_discount ?? 0;
    const shippingRemaining = Math.max(0, config.freeShippingCents - subtotal);

    return { config, items, ntzQty, rewardLines, subtotal, total, savings, shippingRemaining };
  }

  /* ------------------------------ RENDERING ------------------------------- */
  function updateProgress(m) {
    const root = document.querySelector(selectors.progress);
    if (!root) return;

    // Reward tiers start at 3, so any milestone below that is the value-based
    // free-shipping node. It must be driven by cart total, not NTZ pair count.
    const lowestRewardTier = Math.min(...DEFAULTS.rewards.map((r) => r.tier));
    const shippingUnlocked = m.shippingRemaining === 0;

    let shippingMin = 0;
    root.querySelectorAll(selectors.milestone).forEach((node) => {
      const min = intOr(node.dataset.strutzMilestoneMin, 0);
      if (min < lowestRewardTier && min > shippingMin) shippingMin = min;
    });

    // Let the bar reach the shipping node once its dollar threshold is met,
    // even when the NTZ count alone wouldn't carry it there.
    const effectiveQty = shippingUnlocked ? Math.max(m.ntzQty, shippingMin) : m.ntzQty;
    const fill = Math.min(effectiveQty, m.config.maxTier) / m.config.maxTier * 100;

    root.style.setProperty('--strutz-progress', `${fill}%`);
    const fillNode = root.querySelector(selectors.progressFill);
    if (fillNode) fillNode.style.width = `${fill}%`;

    root.querySelectorAll(selectors.milestone).forEach((node) => {
      const min = intOr(node.dataset.strutzMilestoneMin, 0);
      const isShippingNode = min < lowestRewardTier;
      const unlocked = isShippingNode ? shippingUnlocked : (m.ntzQty >= min);
      node.classList.toggle('is-unlocked', unlocked);
    });

    const status = root.querySelector(selectors.status);
    if (!status) return;

    // Free shipping is the nearer and more universal motivator, so lead with it.
    if (!shippingUnlocked) {
      status.textContent = `You're ${formatMoney(m.shippingRemaining)} away from free shipping.`;
      return;
    }

    const nextReward = DEFAULTS.rewards.find((r) => m.ntzQty < r.tier);
    if (nextReward) {
      const need = nextReward.tier - m.ntzQty;
      status.textContent = `Free shipping unlocked. Add ${need} more NTZ pair${need === 1 ? '' : 's'} to unlock your ${nextReward.name.replace(/^Free\s*/i, '').toLowerCase()}.`;
    } else {
      status.textContent = 'All bundle rewards unlocked — pick your sizes below.';
    }
  }

  function updateBundleRows(m) {
    const card = document.querySelector(selectors.bundleRewards);
    if (!card) return;
    card.querySelectorAll(selectors.tierRow).forEach((row) => {
      const min = intOr(row.dataset.tierMin, 0);
      const active = m.ntzQty >= min;
      row.classList.toggle('is-active', active);
      row.setAttribute('aria-current', active ? 'true' : 'false');
      const check = row.querySelector(selectors.tierCheck);
      if (check) check.hidden = !active;
    });
  }

  function updateTotals(m) {
    document.querySelectorAll(selectors.footerSavings).forEach((n) => { n.textContent = formatMoney(m.savings); });
    document.querySelectorAll(selectors.footerTotal).forEach((n) => { n.textContent = formatMoney(m.total); });
    document.querySelectorAll(selectors.checkoutTotal).forEach((n) => { n.textContent = formatMoney(m.total); });
  }

  // Style any reward line in the cart as free + lock its quantity
  function styleRewardLines() {
    document.querySelectorAll(selectors.lineItem).forEach((node) => {
      const handle = node.dataset.handle || '';
      const isRw = rewardHandles().includes(handle) || node.dataset.rewardSlot;
      if (!isRw) return;
      node.classList.add('strutz-mystery-line', 'strutz-free-reward-line');
      const qty = node.querySelector('input[name="updates[]"], .quantity__input');
      if (qty) qty.disabled = true;
      const price = node.querySelector('.price');
      if (price && !price.querySelector('.strutz-free-price')) {
        price.innerHTML = '<span class="strutz-free-price"><span class="strutz-free-price__original">$29.00</span> FREE</span>';
      }
    });
  }

  function updateMysteryMessage(m) {
    const anyUnlocked = m.ntzQty >= DEFAULTS.rewards[0].tier;
    document.querySelectorAll(selectors.mysteryMessage).forEach((node) => {
      node.hidden = !anyUnlocked;
      if (anyUnlocked) {
        node.innerHTML = '<strong>Father\u2019s Day rewards unlocked.</strong> Choose a size for each free item below.';
      }
    });
  }

  async function renderRewardSelectors(m) {
    const wrapper = document.querySelector(selectors.mysterySelector);
    if (!wrapper) return;

    const unlocked = DEFAULTS.rewards.filter((r) => m.ntzQty >= r.tier);
    wrapper.hidden = unlocked.length === 0;
    if (!unlocked.length) { wrapper.innerHTML = ''; return; }

    const blocks = await Promise.all(unlocked.map(async (reward) => {
      const line = m.rewardLines[reward.slot];
      try {
        const product = await getProduct(reward.handle);
        const variants = (product.variants || []).filter((v) => v.available !== false);
        if (!variants.length) {
          return rewardBlock(reward, '', 'Sold out', true, !!line);
        }
        const current = line?.variantId;
        const options = variants.map((v) =>
          `<option value="${v.id}" ${Number(v.id) === Number(current) ? 'selected' : ''}>${escapeHtml(getVariantLabel(product, v))}</option>`
        ).join('');
        const select = `<select class="strutz-mystery-selector__select" data-strutz-reward-variant ${variants.length ? '' : 'disabled'}>${options}</select>`;
        return rewardBlock(reward, select, line ? 'Update size' : 'Add free item', false, !!line);
      } catch (err) {
        console.warn('[Strutz cart] Reward product unavailable:', reward.handle, err);
        return rewardBlock(reward, '', 'Unavailable', true, !!line);
      }
    }));

    wrapper.innerHTML = blocks.join('');
  }

  function rewardBlock(reward, selectHtml, buttonText, disabled, inCart) {
    return `
      <div class="strutz-mystery-selector__row" data-strutz-reward-row data-slot="${reward.slot}" data-handle="${reward.handle}">
        <div class="strutz-mystery-selector__copy">
          <strong>${escapeHtml(reward.name)}</strong>
          <span>${inCart ? 'In your cart \u2014 change size if needed.' : 'Pick a size to add your free item.'}</span>
        </div>
        <div class="strutz-mystery-selector__controls">
          ${selectHtml || '<span class="strutz-mystery-selector__added">Size selector unavailable.</span>'}
          <button type="button" class="strutz-mystery-selector__button" data-strutz-reward-add ${disabled ? 'disabled' : ''}>${escapeHtml(buttonText)}</button>
        </div>
      </div>`;
  }

  /* --------------------------- REWARD ACTIONS ----------------------------- */
  // Remove rewards the customer no longer qualifies for. (No auto-add.)
  async function enforceRewards(m) {
    if (state.syncing || state.skipEnforce) return;
    // If all items show ntz:false, we're using cart.js fallback (DOM not ready) — skip enforcement
    if (m.items.length > 0 && m.ntzQty === 0 && m.items.some(it => !isReward(it))) return;
    const toRemove = [];
    Object.values(m.rewardLines).forEach((line) => {
      const def = DEFAULTS.rewards.find((r) => r.handle === line.handle) ||
                  DEFAULTS.rewards.find((r) => r.slot === line.rewardSlot);
      if (def && m.ntzQty < def.tier && line.key) toRemove.push(line.key);
      // also clamp any reward qty > 1 down to 1
      else if (def && line.quantity > 1 && line.key) toRemove.push({ key: line.key, qty: 1 });
    });
    if (!toRemove.length) return;
    try {
      state.syncing = true;
      for (const r of toRemove) {
        if (typeof r === 'string') await changeLine(r, 0, false);
        else await changeLine(r.key, r.qty, false);
      }
      const cart = await fetchCartWithSections(getBundledSections());
      state.skipEnforce = true;
      publishCartUpdate(cart, 'strutz-reward-enforce');
      window.setTimeout(() => { state.skipEnforce = false; }, 1500);
    } catch (err) {
      console.warn('[Strutz cart] Reward enforce skipped:', err);
    } finally {
      state.syncing = false;
    }
  }

  async function addReward(reward, variantId) {
    if (!variantId || state.busy) return;
    state.busy = true;
    try {
      // remove existing same-slot reward first (so changing size replaces it)
      const m = getMetrics(state.cart);
      const existing = m.rewardLines[reward.slot];
      if (existing?.key) await changeLine(existing.key, 0, false);

      const payload = {
        items: [{
          id: Number(variantId),
          quantity: 1,
          properties: { _strutz_reward_slot: reward.slot, _strutz_reward: 'true' }
        }],
        sections: getBundledSections()
      };
      const cart = await addToCartAndPublish(payload, 'strutz-reward');
      state.cart = cart;
      // Refresh v3-only UI without enforce (we just added a valid reward). The native
      // cartUpdate event has already swapped the drawer section HTML.
      state.skipEnforce = true;
      await refresh(cart, { enforce: false });
      // Allow enforce again after theme has re-rendered
      window.setTimeout(() => { state.skipEnforce = false; }, 2000);
    } catch (err) {
      console.warn('[Strutz cart] Reward add failed:', err);
    } finally {
      state.busy = false;
    }
  }

  function onRewardAddClick(event) {
    const button = event.target.closest('[data-strutz-reward-add]');
    if (!button) return;
    const row = button.closest('[data-strutz-reward-row]');
    if (!row) return;
    const reward = DEFAULTS.rewards.find((r) => r.slot === row.dataset.slot);
    const select = row.querySelector('[data-strutz-reward-variant]');
    const variantId = Number(select?.value || 0);
    if (!reward || !variantId) return;
    button.classList.add('is-loading');
    button.disabled = true;
    addReward(reward, variantId).finally(() => {
      button.classList.remove('is-loading');
      button.disabled = false;
    });
  }

  /* ------------------------------ UPSELLS --------------------------------- */
  /* (unchanged from v2 — its selectors already match the Liquid)            */
  function chooseUpsellCollection(m) {
    const items = m.items;
    const hasNtz = items.some((it) => it.ntz);
    if (hasNtz) return m.config.ntzHandle;
    return m.config.allHandle;
  }
  async function fetchCollectionProducts(handle, config) {
    if (state.upsellCache.has(handle)) return state.upsellCache.get(handle);
    const root = window.theme?.routes?.root_url || '/';
    const url = handle === config.allHandle
      ? `${root}products.json?limit=24`
      : `${root}collections/${handle}/products.json?limit=24&sort_by=created-descending`;
    const res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`Unable to fetch collection: ${handle}`);
    const data = await res.json();
    const products = data.products || [];
    state.upsellCache.set(handle, products);
    return products;
  }
  async function updateUpsells(m) {
    const wrapper = document.querySelector(selectors.upsells);
    if (!wrapper) return;
    const present = new Set(m.items.map((it) => it.handle).filter(Boolean));
    const handle = chooseUpsellCollection(m);
    const key = `${handle}|${Array.from(present).sort().join(',')}`;
    if (state.lastUpsellKey === key && wrapper.dataset.loaded === 'true') return;
    state.lastUpsellKey = key;
    try {
      const products = await fetchCollectionProducts(handle, m.config);
      const filtered = products
        .filter((p) => p && p.handle && !present.has(p.handle) && !rewardHandles().includes(p.handle))
        .slice(0, 3);
      renderUpsells(wrapper, filtered);
      wrapper.dataset.loaded = 'true';
    } catch (err) {
      console.warn('[Strutz cart] Upsells unavailable:', err);
      wrapper.innerHTML = '';
    }
  }
  function renderUpsells(wrapper, products) {
    if (!products.length) { wrapper.innerHTML = ''; return; }
    wrapper.innerHTML = `
      <div class="strutz-upsells__header">
        <p class="strutz-upsells__eyebrow">Complete the bundle</p>
        <p class="strutz-upsells__subtext">Customer favorites to round out the order.</p>
      </div>
      <div class="strutz-upsells__grid">
        ${products.map(upsellMarkup).join('')}
      </div>`;
  }
  function upsellMarkup(product) {
    const variants = (product.variants || []).filter((v) => v.available !== false);
    const options = variants.map((v) =>
      `<option value="${v.id}">${escapeHtml(getVariantLabel(product, v))} \u2014 ${formatMoney(Number(v.price || 0) * 100)}</option>`).join('');
    const image = product.images?.[0]?.src || product.image?.src || '';
    const price = variants[0] ? formatMoney(Number(variants[0].price || 0) * 100) : '';
    return `
      <article class="strutz-upsell" data-upsell-handle="${escapeHtml(product.handle)}">
        ${image ? `<img class="strutz-upsell__image" src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" loading="lazy">` : '<div class="strutz-upsell__image"></div>'}
        <div class="strutz-upsell__body">
          <p class="strutz-upsell__title">${escapeHtml(product.title)}</p>
          <p class="strutz-upsell__price">${price}</p>
          <div class="strutz-upsell__actions">
            <select class="strutz-upsell__select" data-strutz-upsell-variant>${options}</select>
            <button type="button" class="strutz-upsell__button" data-strutz-upsell-add ${variants.length ? '' : 'disabled'}>${variants.length ? 'Add' : 'Sold out'}</button>
          </div>
        </div>
      </article>`;
  }
  async function onUpsellAddClick(event) {
    const button = event.target.closest('[data-strutz-upsell-add]');
    if (!button || button.disabled || button.dataset.strutzPending === 'true' || state.busy) return;
    const card = button.closest('.strutz-upsell');
    const variantId = Number(card?.querySelector('[data-strutz-upsell-variant]')?.value || 0);
    if (!variantId) return;

    const originalText = button.textContent;
    state.busy = true;
    button.dataset.strutzPending = 'true';
    button.classList.add('is-loading');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Adding...';

    try {
      await addToCartAndPublish({ items: [{ id: variantId, quantity: 1 }], sections: getBundledSections() }, 'strutz-upsell');

      // Give cart.js one frame to swap the drawer section before allowing another tap.
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (button.isConnected) {
        button.classList.remove('is-loading');
        button.textContent = 'Added \u2713';
      }
    } catch (err) {
      console.warn('[Strutz cart] Upsell add failed:', err);
      if (button.isConnected) button.textContent = originalText || 'Add';
    } finally {
      state.busy = false;
      if (button.isConnected) {
        window.setTimeout(() => {
          if (!button.isConnected) return;
          button.dataset.strutzPending = 'false';
          button.removeAttribute('aria-busy');
          button.classList.remove('is-loading');
          button.disabled = false;
          if (button.textContent === 'Added \u2713') button.textContent = originalText || 'Add';
        }, 700);
      }
    }
  }

  function setupCheckoutFeedback() {
    document.querySelectorAll(selectors.checkout).forEach((button) => {
      if (button.dataset.strutzBound === 'true') return;
      button.dataset.strutzBound = 'true';
      button.addEventListener('click', () => {
        button.classList.add('is-loading');
        button.setAttribute('aria-busy', 'true');
      });
    });
  }

  /* ------------------------------ ORCHESTRATE ----------------------------- */
  async function refresh(cart = state.cart, options = {}) {
    try {
      if (!cart) cart = await getCart();
      state.cart = cart;
      const m = getMetrics(cart);
      updateProgress(m);
      updateBundleRows(m);
      updateTotals(m);
      updateMysteryMessage(m);
      styleRewardLines();
      await renderRewardSelectors(m);
      setupCheckoutFeedback();
      updateUpsells(m);
      if (options.enforce !== false) enforceRewards(m); // auto-remove only
    } catch (err) {
      console.warn('[Strutz cart] refresh failed:', err);
    }
  }

  document.addEventListener('DOMContentLoaded', () => refresh(null, { enforce: true }));
  document.addEventListener('cart:updated', (e) => {
    const source = e.detail?.source || '';
    // Skip enforce if this event was fired by our own reward add/enforce to prevent loops
    const shouldEnforce = !source.startsWith('strutz-reward');
    refresh(e.detail?.cart, { enforce: shouldEnforce });
  });
  document.addEventListener('cart:refresh', (e) => {
    const source = e.detail?.source || '';
    const shouldEnforce = !source.startsWith('strutz-reward');
    window.setTimeout(() => { state.cart = null; refresh(null, { enforce: shouldEnforce }); }, 250);
  });
  document.addEventListener('click', onRewardAddClick);
  document.addEventListener('click', onUpsellAddClick);
})();